import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import mammoth from 'mammoth';
import { PDFDocument } from 'pdf-lib';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

export class PDFConverter {
  
  // Helper function to get compression settings
  static getCompressionSettings(options) {
    const compressionLevel = options.compression !== undefined ? options.compression : 80;
    
    if (compressionLevel === 0) {
      // 0% = No compression at all
      return {
        pdfCompress: false,
        imageQuality: 1.0
      };
    } else {
      // 1-100% = Varying levels of compression
      return {
        pdfCompress: true,
        imageQuality: Math.max(0.1, 1.0 - (compressionLevel / 100) * 0.9)
      };
    }
  }
  
  // Convert image to PDF
  static async convertImageToPDF(file, options = {}) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const img = new Image();
          img.onload = () => {
            const compressionSettings = this.getCompressionSettings(options);
            
            const pdf = new jsPDF({
              orientation: options.orientation || 'portrait',
              unit: 'mm',
              format: options.format || 'a4',
              compress: compressionSettings.pdfCompress
            });

            // Get page dimensions and set margins
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 15; // 15mm margins on all sides
            const maxWidth = pageWidth - (2 * margin);
            const maxHeight = pageHeight - (2 * margin);
            
            // Calculate image dimensions to fit within page with margins
            let imgWidth, imgHeight;
            const originalRatio = img.width / img.height;
            
            // Try fitting by width first
            imgWidth = maxWidth;
            imgHeight = imgWidth / originalRatio;
            
            // If height exceeds available space, fit by height instead
            if (imgHeight > maxHeight) {
              imgHeight = maxHeight;
              imgWidth = imgHeight * originalRatio;
            }
            
            // Center the image on the page
            const x = (pageWidth - imgWidth) / 2;
            const y = (pageHeight - imgHeight) / 2;
            
            let finalImageData;
            
            if (compressionSettings.imageQuality < 1.0) {
              // Create a canvas to compress the image
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = img.width;
              canvas.height = img.height;
              ctx.drawImage(img, 0, 0);
              
              // Get compressed image data
              finalImageData = canvas.toDataURL('image/jpeg', compressionSettings.imageQuality);
            } else {
              // Use original image data (no compression)
              finalImageData = event.target.result;
            }
            
            pdf.addImage(finalImageData, 'JPEG', x, y, imgWidth, imgHeight);
            resolve(pdf);
          };
          img.onerror = reject;
          img.src = event.target.result;
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Convert text to PDF
  static async convertTextToPDF(file, options = {}) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          const compressionSettings = this.getCompressionSettings(options);
          
          const pdf = new jsPDF({
            orientation: options.orientation || 'portrait',
            unit: 'mm',
            format: options.format || 'a4',
            compress: compressionSettings.pdfCompress
          });

          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const margin = 15; // Reduced margin for more space
          const maxLineWidth = pageWidth - 2 * margin;
          
          // Dynamic font size and line height based on content length
          const fontSize = options.fontSize || 12;
          const lineHeight = options.lineHeight || (fontSize * 0.6); // More optimal line height
          
          pdf.setFontSize(fontSize);
          
          const lines = pdf.splitTextToSize(text, maxLineWidth);
          
          // Calculate if content fits on one page and adjust accordingly
          const totalTextHeight = lines.length * lineHeight;
          const availableHeight = pageHeight - (2 * margin);
          
          let startY = margin;
          
          // If content is shorter than page, center it vertically
          if (totalTextHeight < availableHeight) {
            startY = (pageHeight - totalTextHeight) / 2;
          }
          
          let y = startY;
          
          lines.forEach((line, index) => {
            // Check if we need a new page
            if (y + lineHeight > pageHeight - margin) {
              pdf.addPage();
              y = margin; // Start fresh on new page
            }
            
            pdf.text(line, margin, y);
            y += lineHeight;
          });

          resolve(pdf);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  // Convert DOCX to PDF using mammoth
  static async convertDocxToPDF(file, options = {}) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target.result;
          const result = await mammoth.convertToHtml({ arrayBuffer });
          
          // Create a temporary div to render HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = result.value;
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.width = '800px';
          tempDiv.style.padding = '20px'; // Reduced padding for better fit
          tempDiv.style.backgroundColor = 'white';
          tempDiv.style.fontFamily = 'Arial, sans-serif';
          tempDiv.style.fontSize = '14px';
          tempDiv.style.lineHeight = '1.6';
          
          document.body.appendChild(tempDiv);

          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff'
          });

          document.body.removeChild(tempDiv);

          const pdf = new jsPDF({
            orientation: options.orientation || 'portrait',
            unit: 'mm',
            format: options.format || 'a4',
            compress: this.getCompressionSettings(options).pdfCompress
          });

          // Get page dimensions and set margins
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const margin = 15; // 15mm margins on all sides
          const maxWidth = pageWidth - (2 * margin);
          const maxHeight = pageHeight - (2 * margin);
          
          // Calculate image dimensions to fit within page with margins
          let imgWidth, imgHeight;
          const originalRatio = canvas.width / canvas.height;
          
          // Try fitting by width first
          imgWidth = maxWidth;
          imgHeight = imgWidth / originalRatio;
          
          // If height exceeds available space, fit by height instead
          if (imgHeight > maxHeight) {
            imgHeight = maxHeight;
            imgWidth = imgHeight * originalRatio;
          }
          
          // Center the image on the page
          const x = (pageWidth - imgWidth) / 2;
          const y = (pageHeight - imgHeight) / 2;
          
          // Apply compression based on quality level
          // 0% = no compression (quality 1.0), 100% = max compression (quality 0.1)
          const compressionLevel = options.compression !== undefined ? options.compression : 80;
          const compressionQuality = compressionLevel === 0 ? 1.0 : Math.max(0.1, 1.0 - (compressionLevel / 100) * 0.9);
          const compressedDataUrl = canvas.toDataURL('image/png', compressionQuality);
          
          pdf.addImage(compressedDataUrl, 'PNG', x, y, imgWidth, imgHeight);
          resolve(pdf);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // Convert HTML to PDF
  static async convertHtmlToPDF(file, options = {}) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const htmlContent = event.target.result;
          
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlContent;
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.width = '800px';
          tempDiv.style.padding = '20px'; // Reduced padding for better fit
          tempDiv.style.backgroundColor = 'white';
          
          document.body.appendChild(tempDiv);

          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff'
          });

          document.body.removeChild(tempDiv);

          const pdf = new jsPDF({
            orientation: options.orientation || 'portrait',
            unit: 'mm',
            format: options.format || 'a4',
            compress: this.getCompressionSettings(options).pdfCompress
          });

          // Get page dimensions and set margins
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const margin = 15; // 15mm margins on all sides
          const maxWidth = pageWidth - (2 * margin);
          const maxHeight = pageHeight - (2 * margin);
          
          // Calculate image dimensions to fit within page with margins
          let imgWidth, imgHeight;
          const originalRatio = canvas.width / canvas.height;
          
          // Try fitting by width first
          imgWidth = maxWidth;
          imgHeight = imgWidth / originalRatio;
          
          // If height exceeds available space, fit by height instead
          if (imgHeight > maxHeight) {
            imgHeight = maxHeight;
            imgWidth = imgHeight * originalRatio;
          }
          
          // Center the image on the page
          const x = (pageWidth - imgWidth) / 2;
          const y = (pageHeight - imgHeight) / 2;
          
          // Apply compression based on quality level
          // 0% = no compression (quality 1.0), 100% = max compression (quality 0.1)
          const compressionLevel = options.compression !== undefined ? options.compression : 80;
          const compressionQuality = compressionLevel === 0 ? 1.0 : Math.max(0.1, 1.0 - (compressionLevel / 100) * 0.9);
          const compressedDataUrl = canvas.toDataURL('image/png', compressionQuality);
          
          pdf.addImage(compressedDataUrl, 'PNG', x, y, imgWidth, imgHeight);
          resolve(pdf);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  // Convert Excel (.xlsx) to PDF
  static async convertXlsxToPDF(file, options = {}) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target.result;
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          
          // Get the first sheet or all sheets if multiple
          const sheetNames = workbook.SheetNames;
          if (sheetNames.length === 0) {
            throw new Error('No sheets found in Excel file');
          }

          const pdf = new jsPDF({
            orientation: options.orientation || 'landscape', // Landscape is better for spreadsheets
            unit: 'mm',
            format: options.format || 'a4',
            compress: this.getCompressionSettings(options).pdfCompress
          });

          let isFirstSheet = true;

          for (const sheetName of sheetNames) {
            const worksheet = workbook.Sheets[sheetName];
            
            // Convert worksheet to HTML
            const htmlString = XLSX.utils.sheet_to_html(worksheet, {
              table: true,
              header: `<h2>${sheetName}</h2>`,
            });

            // Create a temporary div to render the HTML table
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlString;
            tempDiv.style.position = 'absolute';
            tempDiv.style.left = '-9999px';
            tempDiv.style.width = '1200px'; // Wider for spreadsheets
            tempDiv.style.padding = '20px';
            tempDiv.style.backgroundColor = 'white';
            tempDiv.style.fontFamily = 'Arial, sans-serif';
            tempDiv.style.fontSize = '10px'; // Smaller font for spreadsheets
            tempDiv.style.lineHeight = '1.2';
            
            // Style the table
            const table = tempDiv.querySelector('table');
            if (table) {
              table.style.borderCollapse = 'collapse';
              table.style.width = '100%';
              table.style.border = '1px solid #ccc';
              
              // Style table cells
              const cells = table.querySelectorAll('td, th');
              cells.forEach(cell => {
                cell.style.border = '1px solid #ddd';
                cell.style.padding = '4px';
                cell.style.fontSize = '9px';
                cell.style.textAlign = 'left';
              });

              // Style header cells
              const headers = table.querySelectorAll('th');
              headers.forEach(header => {
                header.style.backgroundColor = '#f5f5f5';
                header.style.fontWeight = 'bold';
              });
            }
            
            document.body.appendChild(tempDiv);

            const canvas = await html2canvas(tempDiv, {
              scale: 1.5,
              useCORS: true,
              backgroundColor: '#ffffff'
            });

            document.body.removeChild(tempDiv);

            if (!isFirstSheet) {
              pdf.addPage();
            }

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth - 20; // 10mm margin on each side
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Apply compression based on quality level
            // 0% = no compression (quality 1.0), 100% = max compression (quality 0.1)
            const compressionLevel = options.compression !== undefined ? options.compression : 80;
            const compressionQuality = compressionLevel === 0 ? 1.0 : Math.max(0.1, 1.0 - (compressionLevel / 100) * 0.9);
            const compressedDataUrl = canvas.toDataURL('image/png', compressionQuality);
            
            // If content is taller than page, scale it to fit
            if (imgHeight > pageHeight - 40) { // 20mm margin top and bottom
              const scaledHeight = pageHeight - 40;
              const scaledWidth = (canvas.width * scaledHeight) / canvas.height;
              pdf.addImage(
                compressedDataUrl, 
                'PNG', 
                (pageWidth - scaledWidth) / 2, 
                20, 
                scaledWidth, 
                scaledHeight
              );
            } else {
              pdf.addImage(
                compressedDataUrl, 
                'PNG', 
                10, 
                20, 
                imgWidth, 
                imgHeight
              );
            }

            isFirstSheet = false;
          }

          resolve(pdf);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // Convert PowerPoint (.pptx) to PDF
  static async convertPptxToPDF(file, options = {}) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target.result;
          const zip = new JSZip();
          const zipFile = await zip.loadAsync(arrayBuffer);

          // Extract text content from slides
          let slideNumber = 1;

          // Look for slide files in the ppt/slides/ directory
          const slidePromises = [];
          zipFile.forEach((relativePath, file) => {
            if (relativePath.startsWith('ppt/slides/slide') && relativePath.endsWith('.xml')) {
              slidePromises.push(
                file.async('text').then(content => ({
                  slideNumber: slideNumber++,
                  content: this.extractTextFromSlideXML(content)
                }))
              );
            }
          });

          const slides = await Promise.all(slidePromises);
          slides.sort((a, b) => a.slideNumber - b.slideNumber);

          if (slides.length === 0) {
            // Fallback: try to find any text content
            const fallbackContent = await this.extractFallbackPptxContent(zipFile);
            if (fallbackContent) {
              slides.push({ slideNumber: 1, content: fallbackContent });
            } else {
              throw new Error('No slides found in PowerPoint file');
            }
          }

          const pdf = new jsPDF({
            orientation: options.orientation || 'landscape', // Landscape for presentations
            unit: 'mm',
            format: options.format || 'a4',
            compress: this.getCompressionSettings(options).pdfCompress
          });

          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const margin = 20;
          const maxLineWidth = pageWidth - 2 * margin;

          let isFirstSlide = true;

          for (const slide of slides) {
            if (!isFirstSlide) {
              pdf.addPage();
            }

            // Add slide title
            pdf.setFontSize(16);
            pdf.setFont(undefined, 'bold');
            pdf.text(`Slide ${slide.slideNumber}`, margin, margin + 10);

            // Add slide content
            pdf.setFontSize(options.fontSize || 12);
            pdf.setFont(undefined, 'normal');
            
            const lines = pdf.splitTextToSize(slide.content, maxLineWidth);
            let y = margin + 25;
            const lineHeight = options.lineHeight || 7;

            lines.forEach((line) => {
              if (y + lineHeight > pageHeight - margin) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin, y);
              y += lineHeight;
            });

            isFirstSlide = false;
          }

          resolve(pdf);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // Helper method to extract text from slide XML
  static extractTextFromSlideXML(xmlContent) {
    try {
      // Simple regex-based text extraction from PowerPoint XML
      // This extracts content between <a:t> tags (text runs)
      const textMatches = xmlContent.match(/<a:t[^>]*>(.*?)<\/a:t>/g);
      if (textMatches) {
        return textMatches
          .map(match => match.replace(/<a:t[^>]*>(.*?)<\/a:t>/, '$1'))
          .map(text => text.trim())
          .filter(text => text.length > 0)
          .join(' ');
      }

      // Fallback: extract any text content
      const fallbackMatches = xmlContent.match(/>[^<]+</g);
      if (fallbackMatches) {
        return fallbackMatches
          .map(match => match.slice(1, -1).trim())
          .filter(text => text.length > 0 && !text.match(/^[0-9\s\-_]+$/))
          .join(' ');
      }

      return 'Unable to extract text content from this slide.';
    } catch (error) {
      return 'Error extracting slide content.';
    }
  }

  // Fallback method for extracting PPTX content
  static async extractFallbackPptxContent(zipFile) {
    try {
      // Try to find presentation.xml or core.xml for basic info
      const coreXml = zipFile.file('docProps/core.xml');
      if (coreXml) {
        const content = await coreXml.async('text');
        const titleMatch = content.match(/<dc:title>(.*?)<\/dc:title>/);
        const subjectMatch = content.match(/<dc:subject>(.*?)<\/dc:subject>/);
        
        let fallbackText = 'PowerPoint Presentation\n\n';
        if (titleMatch) fallbackText += `Title: ${titleMatch[1]}\n`;
        if (subjectMatch) fallbackText += `Subject: ${subjectMatch[1]}\n`;
        
        return fallbackText + '\nNote: This is a PowerPoint file. Some content may not be fully extracted.';
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  // Main conversion method
  static async convertToPDF(file, options = {}) {
    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    try {
      let pdf;

      if (fileType.startsWith('image/')) {
        pdf = await this.convertImageToPDF(file, options);
      } else if (fileType === 'text/plain' || fileName.endsWith('.txt')) {
        pdf = await this.convertTextToPDF(file, options);
      } else if (fileName.endsWith('.docx')) {
        pdf = await this.convertDocxToPDF(file, options);
      } else if (fileName.endsWith('.xlsx') || fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        pdf = await this.convertXlsxToPDF(file, options);
      } else if (fileName.endsWith('.pptx') || fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
        pdf = await this.convertPptxToPDF(file, options);
      } else if (fileType === 'text/html' || fileName.endsWith('.html')) {
        pdf = await this.convertHtmlToPDF(file, options);
      } else if (fileName.endsWith('.md')) {
        // Treat markdown as plain text for now
        pdf = await this.convertTextToPDF(file, options);
      } else {
        throw new Error(`Unsupported file type: ${fileType || 'unknown'}`);
      }

      return pdf;
    } catch (error) {
      throw new Error(`Failed to convert ${file.name}: ${error.message}`);
    }
  }

  // Download PDF
  static downloadPDF(pdf, filename = 'converted.pdf') {
    pdf.save(filename);
  }

  // Get PDF as blob
  static getPDFBlob(pdf) {
    return pdf.output('blob');
  }

  // Create a single PDF from multiple content sources
  static async createMergedPDF(files, options = {}, onProgress = null) {
    const mergedPDF = new jsPDF({
      orientation: options.orientation || 'portrait',
      unit: 'mm',
      format: options.format || 'a4',
      compress: true
    });

    let isFirstPage = true;
    const errors = [];
    let processedFiles = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i].file || files[i];
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      if (onProgress) {
        onProgress(i, files.length, file, 'converting');
      }

      try {
        if (fileType.startsWith('image/')) {
          await this.addImageToMergedPDF(mergedPDF, file, isFirstPage, options);
        } else if (fileType === 'text/plain' || fileName.endsWith('.txt') || fileName.endsWith('.md')) {
          await this.addTextToMergedPDF(mergedPDF, file, isFirstPage, options);
        } else if (fileName.endsWith('.docx')) {
          await this.addDocxToMergedPDF(mergedPDF, file, isFirstPage, options);
        } else if (fileName.endsWith('.xlsx') || fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          await this.addXlsxToMergedPDF(mergedPDF, file, isFirstPage, options);
        } else if (fileName.endsWith('.pptx') || fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
          await this.addPptxToMergedPDF(mergedPDF, file, isFirstPage, options);
        } else if (fileType === 'text/html' || fileName.endsWith('.html')) {
          await this.addHtmlToMergedPDF(mergedPDF, file, isFirstPage, options);
        } else {
          console.warn(`Skipping unsupported file type: ${fileType || 'unknown'} for ${file.name}`);
          errors.push({
            file: files[i],
            error: `Unsupported file type: ${fileType || 'unknown'}`
          });
          continue;
        }
        isFirstPage = false;
        processedFiles++;
      } catch (error) {
        console.warn(`Failed to add ${file.name} to merged PDF:`, error);
        errors.push({
          file: files[i],
          error: error.message
        });
        // Continue with other files even if one fails
      }
    }

    return { 
      pdf: mergedPDF, 
      errors: errors,
      processedFiles: processedFiles,
      totalFiles: files.length
    };
  }

  // Add image to merged PDF
  static async addImageToMergedPDF(mergedPDF, file, isFirstPage, options) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const img = new Image();
          img.onload = () => {
            if (!isFirstPage) {
              mergedPDF.addPage();
            }

            const pageWidth = mergedPDF.internal.pageSize.getWidth();
            const pageHeight = mergedPDF.internal.pageSize.getHeight();
            const imgWidth = pageWidth;
            const imgHeight = (img.height * imgWidth) / img.width;

            // Apply compression based on quality level
            // 0% = no compression (quality 1.0), 100% = max compression (quality 0.1)
            const compressionLevel = options.compression !== undefined ? options.compression : 80;
            const compressionQuality = compressionLevel === 0 ? 1.0 : Math.max(0.1, 1.0 - (compressionLevel / 100) * 0.9);
            
            // Create a canvas to compress the image
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            
            // Get compressed image data
            const compressedDataUrl = canvas.toDataURL('image/jpeg', compressionQuality);
            
            // If image is taller than page, scale it down
            if (imgHeight > pageHeight) {
              const scaledHeight = pageHeight;
              const scaledWidth = (img.width * scaledHeight) / img.height;
              mergedPDF.addImage(compressedDataUrl, 'JPEG', (pageWidth - scaledWidth) / 2, 0, scaledWidth, scaledHeight);
            } else {
              mergedPDF.addImage(compressedDataUrl, 'JPEG', 0, (pageHeight - imgHeight) / 2, imgWidth, imgHeight);
            }

            resolve();
          };
          img.onerror = reject;
          img.src = event.target.result;
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Add text to merged PDF
  static async addTextToMergedPDF(mergedPDF, file, isFirstPage, options) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          
          if (!isFirstPage) {
            mergedPDF.addPage();
          }

          const pageWidth = mergedPDF.internal.pageSize.getWidth();
          const pageHeight = mergedPDF.internal.pageSize.getHeight();
          const margin = 20;
          const maxLineWidth = pageWidth - 2 * margin;
          
          mergedPDF.setFontSize(options.fontSize || 12);
          
          const lines = mergedPDF.splitTextToSize(text, maxLineWidth);
          let y = margin;
          const lineHeight = options.lineHeight || 7;
          
          lines.forEach((line) => {
            if (y + lineHeight > pageHeight - margin) {
              mergedPDF.addPage();
              y = margin;
            }
            mergedPDF.text(line, margin, y);
            y += lineHeight;
          });

          resolve();
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  // Add DOCX to merged PDF
  static async addDocxToMergedPDF(mergedPDF, file, isFirstPage, options) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target.result;
          const result = await mammoth.convertToHtml({ arrayBuffer });
          
          if (!isFirstPage) {
            mergedPDF.addPage();
          }

          // Create a temporary div to render HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = result.value;
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.width = '800px';
          tempDiv.style.padding = '40px';
          tempDiv.style.backgroundColor = 'white';
          tempDiv.style.fontFamily = 'Arial, sans-serif';
          tempDiv.style.fontSize = '14px';
          tempDiv.style.lineHeight = '1.6';
          
          document.body.appendChild(tempDiv);

          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff'
          });

          document.body.removeChild(tempDiv);

          const pageWidth = mergedPDF.internal.pageSize.getWidth();
          const pageHeight = mergedPDF.internal.pageSize.getHeight();
          const imgWidth = pageWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // If content is taller than one page, we might need multiple pages
          if (imgHeight > pageHeight) {
            // For now, just scale it down to fit on one page
            const scaledHeight = pageHeight;
            const scaledWidth = (canvas.width * scaledHeight) / canvas.height;
            mergedPDF.addImage(canvas.toDataURL('image/png'), 'PNG', (pageWidth - scaledWidth) / 2, 0, scaledWidth, scaledHeight);
          } else {
            mergedPDF.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight);
          }

          resolve();
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // Add HTML to merged PDF
  static async addHtmlToMergedPDF(mergedPDF, file, isFirstPage, options) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const htmlContent = event.target.result;
          
          if (!isFirstPage) {
            mergedPDF.addPage();
          }
          
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlContent;
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.width = '800px';
          tempDiv.style.padding = '40px';
          tempDiv.style.backgroundColor = 'white';
          
          document.body.appendChild(tempDiv);

          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff'
          });

          document.body.removeChild(tempDiv);

          const pageWidth = mergedPDF.internal.pageSize.getWidth();
          const pageHeight = mergedPDF.internal.pageSize.getHeight();
          const imgWidth = pageWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          if (imgHeight > pageHeight) {
            const scaledHeight = pageHeight;
            const scaledWidth = (canvas.width * scaledHeight) / canvas.height;
            mergedPDF.addImage(canvas.toDataURL('image/png'), 'PNG', (pageWidth - scaledWidth) / 2, 0, scaledWidth, scaledHeight);
          } else {
            mergedPDF.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight);
          }

          resolve();
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  // Add Excel (.xlsx) to merged PDF
  static async addXlsxToMergedPDF(mergedPDF, file, isFirstPage, options) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target.result;
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          
          const sheetNames = workbook.SheetNames;
          if (sheetNames.length === 0) {
            throw new Error('No sheets found in Excel file');
          }

          let isFirstSheetInFile = isFirstPage;

          for (const sheetName of sheetNames) {
            const worksheet = workbook.Sheets[sheetName];
            
            if (!isFirstSheetInFile) {
              mergedPDF.addPage();
            }
            
            // Convert worksheet to HTML
            const htmlString = XLSX.utils.sheet_to_html(worksheet, {
              table: true,
              header: `<h2>${sheetName}</h2>`,
            });

            // Create a temporary div to render the HTML table
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlString;
            tempDiv.style.position = 'absolute';
            tempDiv.style.left = '-9999px';
            tempDiv.style.width = '1200px';
            tempDiv.style.padding = '20px';
            tempDiv.style.backgroundColor = 'white';
            tempDiv.style.fontFamily = 'Arial, sans-serif';
            tempDiv.style.fontSize = '10px';
            tempDiv.style.lineHeight = '1.2';
            
            // Style the table
            const table = tempDiv.querySelector('table');
            if (table) {
              table.style.borderCollapse = 'collapse';
              table.style.width = '100%';
              table.style.border = '1px solid #ccc';
              
              const cells = table.querySelectorAll('td, th');
              cells.forEach(cell => {
                cell.style.border = '1px solid #ddd';
                cell.style.padding = '4px';
                cell.style.fontSize = '9px';
                cell.style.textAlign = 'left';
              });

              const headers = table.querySelectorAll('th');
              headers.forEach(header => {
                header.style.backgroundColor = '#f5f5f5';
                header.style.fontWeight = 'bold';
              });
            }
            
            document.body.appendChild(tempDiv);

            const canvas = await html2canvas(tempDiv, {
              scale: 1.5,
              useCORS: true,
              backgroundColor: '#ffffff'
            });

            document.body.removeChild(tempDiv);

            const pageWidth = mergedPDF.internal.pageSize.getWidth();
            const pageHeight = mergedPDF.internal.pageSize.getHeight();
            const imgWidth = pageWidth - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            if (imgHeight > pageHeight - 40) {
              const scaledHeight = pageHeight - 40;
              const scaledWidth = (canvas.width * scaledHeight) / canvas.height;
              mergedPDF.addImage(
                canvas.toDataURL('image/png'), 
                'PNG', 
                (pageWidth - scaledWidth) / 2, 
                20, 
                scaledWidth, 
                scaledHeight
              );
            } else {
              mergedPDF.addImage(
                canvas.toDataURL('image/png'), 
                'PNG', 
                10, 
                20, 
                imgWidth, 
                imgHeight
              );
            }

            isFirstSheetInFile = false;
          }

          resolve();
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // Add PowerPoint (.pptx) to merged PDF
  static async addPptxToMergedPDF(mergedPDF, file, isFirstPage, options) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target.result;
          const zip = new JSZip();
          const zipFile = await zip.loadAsync(arrayBuffer);

          // Extract text content from slides
          const slidePromises = [];
          let slideNumber = 1;

          zipFile.forEach((relativePath, file) => {
            if (relativePath.startsWith('ppt/slides/slide') && relativePath.endsWith('.xml')) {
              slidePromises.push(
                file.async('text').then(content => ({
                  slideNumber: slideNumber++,
                  content: this.extractTextFromSlideXML(content)
                }))
              );
            }
          });

          const slides = await Promise.all(slidePromises);
          slides.sort((a, b) => a.slideNumber - b.slideNumber);

          if (slides.length === 0) {
            const fallbackContent = await this.extractFallbackPptxContent(zipFile);
            if (fallbackContent) {
              slides.push({ slideNumber: 1, content: fallbackContent });
            } else {
              throw new Error('No slides found in PowerPoint file');
            }
          }

          let isFirstSlideInFile = isFirstPage;

          for (const slide of slides) {
            if (!isFirstSlideInFile) {
              mergedPDF.addPage();
            }

            const pageWidth = mergedPDF.internal.pageSize.getWidth();
            const pageHeight = mergedPDF.internal.pageSize.getHeight();
            const margin = 20;
            const maxLineWidth = pageWidth - 2 * margin;

            // Add slide title
            mergedPDF.setFontSize(16);
            mergedPDF.setFont(undefined, 'bold');
            mergedPDF.text(`Slide ${slide.slideNumber}`, margin, margin + 10);

            // Add slide content
            mergedPDF.setFontSize(options.fontSize || 12);
            mergedPDF.setFont(undefined, 'normal');
            
            const lines = mergedPDF.splitTextToSize(slide.content, maxLineWidth);
            let y = margin + 25;
            const lineHeight = options.lineHeight || 7;

            lines.forEach((line) => {
              if (y + lineHeight > pageHeight - margin) {
                mergedPDF.addPage();
                y = margin + 10;
              }
              mergedPDF.text(line, margin, y);
              y += lineHeight;
            });

            isFirstSlideInFile = false;
          }

          resolve();
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // Convert multiple files and merge using pdf-lib (more reliable)
  static async convertAndMergeFilesWithPdfLib(files, options = {}, onProgress = null) {
    const pdfDocuments = [];
    const errors = [];
    
    // Step 1: Convert each file to PDF individually
    for (let i = 0; i < files.length; i++) {
      try {
        if (onProgress) {
          onProgress(i, files.length, files[i], 'converting');
        }

        const pdf = await this.convertToPDF(files[i].file || files[i], options);
        
        // Convert jsPDF to pdf-lib format
        const pdfBytes = pdf.output('arraybuffer');
        const pdfDoc = await PDFDocument.load(pdfBytes);
        pdfDocuments.push(pdfDoc);
        
      } catch (error) {
        console.warn(`Failed to convert ${files[i].name}:`, error);
        errors.push({
          file: files[i],
          error: error.message
        });
      }
    }

    if (pdfDocuments.length === 0) {
      return {
        success: false,
        pdf: null,
        errors: errors,
        message: 'No files could be converted to PDF'
      };
    }

    try {
      if (onProgress) {
        onProgress(files.length, files.length, null, 'merging');
      }

      // Step 2: Create a new PDF and merge all documents
      const mergedPdf = await PDFDocument.create();
      
      for (const pdfDoc of pdfDocuments) {
        const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
        pages.forEach((page) => mergedPdf.addPage(page));
      }

      // Step 3: Convert back to jsPDF format for consistency with the rest of the app
      const mergedPdfBytes = await mergedPdf.save();
      const mergedPdfBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      
      // Create a mock jsPDF object for download compatibility
      const mockJsPDF = {
        save: (filename) => {
          const url = URL.createObjectURL(mergedPdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        },
        output: (type) => {
          if (type === 'blob') {
            return mergedPdfBlob;
          }
          return mergedPdfBytes;
        }
      };
      
      return {
        success: true,
        pdf: mockJsPDF,
        errors: errors,
        processedFiles: pdfDocuments.length,
        totalFiles: files.length
      };
    } catch (error) {
      return {
        success: false,
        pdf: null,
        errors: [...errors, { error: `Merge failed: ${error.message}` }],
        message: 'Failed to merge PDFs'
      };
    }
  }

  // Convert multiple files and merge into single PDF
  static async convertAndMergeFiles(files, options = {}, onProgress = null) {
    // Use the more reliable pdf-lib approach
    return await this.convertAndMergeFilesWithPdfLib(files, options, onProgress);
  }

  // Memory management utility
  static cleanupResources(pdfObject) {
    try {
      if (pdfObject && typeof pdfObject.output === 'function') {
        // Revoke any blob URLs if they exist
        const blob = pdfObject.output('blob');
        if (blob instanceof Blob) {
          const url = URL.createObjectURL(blob);
          URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.debug('Resource cleanup warning:', error.message);
    }
  }

  // Batch cleanup for multiple PDF objects
  static cleanupMultipleResources(results) {
    if (Array.isArray(results)) {
      results.forEach(result => {
        if (result.pdf) {
          this.cleanupResources(result.pdf);
        }
      });
    }
  }

  // Batch convert multiple files (individual PDFs)
  static async convertMultipleFiles(files, options = {}, onProgress = null) {
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      try {
        if (onProgress) {
          onProgress(i, files.length, files[i]);
        }

        const pdf = await this.convertToPDF(files[i].file || files[i], options);
        results.push({
          success: true,
          file: files[i],
          pdf: pdf,
          error: null
        });
      } catch (error) {
        results.push({
          success: false,
          file: files[i],
          pdf: null,
          error: error.message
        });
      }
    }

    if (onProgress) {
      onProgress(files.length, files.length, null);
    }

    return results;
  }
}